import { call, get as embeddedGet } from '@ps/web-core-api';
import { Cookie as CookieNames, Currency } from '@ps/web-core-enums';
import type { Website } from '@ps/web-martech-cms-types';
import type { Selector } from '@rematch/select';
import Cookies from 'universal-cookie';

import { select } from '../../../index';
import type {
  EffectParams,
  Model,
  RootState,
  SelectorCreator,
  Slicer,
  UserLimits
} from '../../types';

export interface CurrencyState {
  selected?: Currency;
  selfLimitCurrency?: Currency;
  userLimits?: UserLimits;
}

const SiteCurrency: Partial<{ [key in Website['site']]: Currency }> = {
  CA_ON: Currency.CanadianDollar,
  SPA: Currency.Euro,
  EU: Currency.Euro,
  FRA: Currency.Euro,
  IND: Currency.IndianRupee,
  ITA: Currency.Euro,
  POR: Currency.Euro,
  ROM: Currency.RomanianLeu,
  RU: Currency.RussianRuble,
  SWE: Currency.SwedishKrona,
  UK: Currency.BritishPound,
  CAON: Currency.CanadianDollar,
  ES: Currency.Euro,
  FR: Currency.Euro,
  IN: Currency.IndianRupee,
  IT: Currency.Euro,
  PT: Currency.Euro,
  RO: Currency.RomanianLeu,
  SE: Currency.SwedishKrona
};

const cookies = new Cookies();

const reducers = {
  set: (state: CurrencyState, { selected }: { selected?: Currency }): CurrencyState => ({
    ...state,
    selected
  }),
  setSelfLimitCurrency: (
    state: CurrencyState,
    { selfLimitCurrency }: { selfLimitCurrency: Currency }
  ): CurrencyState => ({
    ...state,
    selfLimitCurrency
  }),
  setUserLimits: (
    state: CurrencyState,
    { userLimits }: { userLimits: UserLimits }
  ): CurrencyState => ({
    ...state,
    userLimits
  })
};

const effects: {
  setPlayMoney({ selected }: { selected: boolean }, params: EffectParams): Promise<void>;
} = {
  async setPlayMoney({ selected }: { selected: boolean }, { dispatch, rootState, select }) {
    if (select.device.isEmbedded(rootState)) {
      call(['app', 'setPlayMoney'], selected);
    } else {
      dispatch.currency.set({ selected: selected ? Currency.PlayMoney : undefined });
      selected
        ? cookies.set(CookieNames.CURRENCY, Currency.PlayMoney)
        : cookies.remove(CookieNames.CURRENCY);
    }
  }
};

const selectors = (slice: Slicer<CurrencyState>, createSelector: SelectorCreator) => ({
  get() {
    return createSelector(
      slice,
      (rootState: RootState) => select.account.getCurrency(rootState),
      (rootState: RootState) => select.cms.getSiteName(rootState),
      ({ selected }, accountCurrency, siteName) =>
        selected || accountCurrency || SiteCurrency[siteName] || Currency.USDollar
    );
  },
  getRealMoney() {
    return createSelector(
      (rootState: RootState) => select.account.getCurrency(rootState),
      (rootState: RootState) => select.cms.getSiteName(rootState),
      (accountCurrency, siteName) => accountCurrency || SiteCurrency[siteName] || Currency.USDollar
    );
  },
  getUserLimits() {
    return slice((state) => state.userLimits);
  },
  isPlayMoney() {
    return slice((state) => state.selected === Currency.PlayMoney);
  },
  getPlayMode() {
    return slice((state) => (state.selected === Currency.PlayMoney ? 'PM' : 'RM'));
  },
  getSelfLimitCurrency(this: { getRealMoney: Selector<RootState, Currency> }) {
    return createSelector(
      slice,
      (rootState: RootState) => select.currency.getRealMoney(rootState),
      ({ selfLimitCurrency }, realMoney) => selfLimitCurrency || realMoney
    );
  }
});

const currency: Model<CurrencyState, typeof reducers, void, typeof effects, typeof selectors> = {
  state: {
    selected: embeddedGet(['app', 'playMoney']) || cookies.get(CookieNames.CURRENCY) || null
  },
  reducers,
  effects,
  selectors
};

export default currency;
